import { Stack } from '@chakra-ui/react';
import { faArrowRight, faFileArrowDown, faFileUser, faPassport, faUnlock } from '@fortawesome/pro-regular-svg-icons';
import { UserRole } from '@/store/auth/types';
import { hasPerm, isAgentOrAgencyRole, isPartnerRole, P } from '@/utilities/roles/permissions';

import { SidebarGroup } from './Group';
import { SidebarAction } from './Action';
import { useSelector } from 'react-redux';
import { ApplicationState } from '@/store';
import { ImportSource } from 'types';
import { getImportOffer } from '@/utilities/helpers';
import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";

interface Props {
  role?: UserRole;
  hasVisaPrepBeenActivated: boolean;
  visaPrepActiveForBrand: boolean;
  documentDownloadsActive: boolean;
  university: string;
  applicantId?: string;

  onPreview: () => void;
  onDownload: (includeHidden?: boolean) => void;
  onUnlockVisaPrep: () => void;
  onPreviewVisaPrep: () => void;
}

function getApplyUrl(
  hasApplyAccess: boolean,
  applicantId: string | undefined,
  applicationId: string | undefined,
  offerId: string | undefined,
  isAgent: boolean,
  isPartner: boolean,
  university: string,
): string {
  let applyUrl = '';

  if (hasApplyAccess && applicantId && offerId) {
    if (isAgent || isPartner) {
      applyUrl = `/apply/application/${university}/${applicantId}/offers/${offerId}`;
    } else if (applicationId) {
      applyUrl = `/application/${university}/${applicantId}/${applicationId}/offers/${offerId}`;
    } else {
      console.error('No application ID or offer ID found');
      return null;
    }
  }

  return applyUrl;
}

function StudentActions(props: Props) {
    const { t } = useTranslation('common');
  const {
    role,
    hasVisaPrepBeenActivated,
    visaPrepActiveForBrand,
    documentDownloadsActive,
    university,
    applicantId,
    onPreview,
    onDownload,
    onUnlockVisaPrep,
    onPreviewVisaPrep,
  } = props;
  const isAgent = isAgentOrAgencyRole(role);
  const isPartner = isPartnerRole(role);
  const importData = useSelector((state: ApplicationState) => state.student.importData);
  const offer = useSelector((state: ApplicationState) => state.student.offer);
  const hasApplyAccess = importData?.source === ImportSource.APPLY || !!importData?.applicantId;
  const currentImportOffer = getImportOffer(importData, offer?.intakeId, offer?.offerId);
  const applicationId = currentImportOffer?.applicationId;
  const offerId = currentImportOffer?.offerId;

  const canViewAllData = role && hasPerm(role, P.VIEW_ALL_DATA);
  const applyUrl = getApplyUrl(hasApplyAccess, applicantId, applicationId, offerId, isAgent, isPartner, university);

  const [isModifierKeyPressed, setIsModifierKeyPressed] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey) {
        setIsModifierKeyPressed(true);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (!event.altKey) {
        setIsModifierKeyPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleOpenApplyRecord = () => {
    if (applyUrl) {
      window.location.href = applyUrl;
    }
  };

  if (!canViewAllData && !documentDownloadsActive && !visaPrepActiveForBrand && !applyUrl) {
    return;
  }

  return (
    <SidebarGroup header="Actions">
      <Stack spacing={0} mx={-3}>
        {canViewAllData && (
          <SidebarAction data-selector={'studentActions-previewButton'} onClick={onPreview} icon={faFileUser}>
            {t('common:viewAllData')}
                                </SidebarAction>
        )}

        {documentDownloadsActive && (
          <SidebarAction
            data-selector={
              isModifierKeyPressed ? 'studentActions-downloadButtonIncludeHidden' : 'studentActions-downloadButton'
            }
            onClick={() => onDownload(isModifierKeyPressed)}
            icon={faFileArrowDown}
          >
            {isModifierKeyPressed ? 'Download all documents (including hidden)' : 'Download all documents'}
          </SidebarAction>
        )}

        {Boolean(applyUrl) && (
          <SidebarAction
            onClick={handleOpenApplyRecord}
            data-selector={'studentActions-openApplyRecButton'}
            icon={faArrowRight}
          >
            {t('common:openRecordInApply')}
                                </SidebarAction>
        )}

        {visaPrepActiveForBrand &&
          (hasVisaPrepBeenActivated ? (
            <SidebarAction
              data-selector={'studentActions-visaPrepButton'}
              onClick={onPreviewVisaPrep}
              icon={faPassport}
            >
              {t('common:preVisaSummary')}
                                  </SidebarAction>
          ) : (
            <SidebarAction data-selector={'studentActions-visaPrepButton'} onClick={onUnlockVisaPrep} icon={faUnlock}>
              {t('common:unlockVisaPreparation')}
                                      </SidebarAction>
          ))}
      </Stack>
    </SidebarGroup>
  );
}

export default StudentActions;
